import React, { useEffect } from "react";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import useTranslation from "../../../shared/hooks/useTranslation";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../shared/hooks/sharedHooks";
import { fetchSessionInit, fetchMachineSessionInit } from "../midasPageSlice";
import MidasMachineWelcome from "./MidasMachineWelcome";
import { dt } from "../../../shared/themes/redesignTokens";

// ===================================================================
//  Design tokens (from approved Paper design)
// ===================================================================

const midas = {
  gold: "#D4A843",
  goldEnd: "#B8902F",
  pillBorder: "#E5E7EB",
  srBlue: "#3B82F6",
  machineGreen: "#22C55E",
  notifAmber: "#F59E0B",
  textDark: "#1a1a2e",
  textGray: "#6B7280",
  textMuted: "#9CA3AF",
  cardText: "#4B5563",
  badgeBg: "#1B2130",
};

// ===================================================================
//  SVG Icons (inline, design-matched)
// ===================================================================

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke={midas.srBlue} strokeWidth="1.3" />
    <path d="M7 4v3.5l2.5 1.5" stroke={midas.srBlue} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MachineIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="2" y="3" width="10" height="8" rx="1.5" stroke={midas.machineGreen} strokeWidth="1.3" />
    <path d="M5 6.5h4M5 8.5h2" stroke={midas.machineGreen} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const BellIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5a3.5 3.5 0 00-3.5 3.5v2.5L2.5 9h9l-1-1.5V5A3.5 3.5 0 007 1.5z" stroke={midas.notifAmber} strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M5.5 9v.5a1.5 1.5 0 003 0V9" stroke={midas.notifAmber} strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const SuggestionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 6l4-3 4 3M4 10l4 3 4-3" stroke={midas.gold} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ===================================================================
//  Suggestion card config (3 cards, no notifications)
// ===================================================================

const platformSuggestions = [
  "midas.welcome.showOpenSRs",
  "midas.welcome.browseBoard",
  "midas.welcome.machineStatus",
];

// ===================================================================
//  MidasWelcome
// ===================================================================

interface MidasWelcomeProps {
  onSuggestionClick: (text: string) => void;
}

const MidasWelcome: React.FC<MidasWelcomeProps> = ({ onSuggestionClick }) => {
  const { translate } = useTranslation("chatBot");
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.account.user);
  const sessionInit = useAppSelector((state) => state.midasPage.sessionInit);
  const sessionInitLoading = useAppSelector(
    (state) => state.midasPage.sessionInitLoading
  );
  const machineContext = useAppSelector(
    (state) => state.chatBot.machineContext
  );
  const machineSessionInit = useAppSelector(
    (state) => state.midasPage.machineSessionInit
  );
  const machineSessionInitLoading = useAppSelector(
    (state) => state.midasPage.machineSessionInitLoading
  );
  const isPhone = useMediaQuery("(max-width:768px)");

  const firstName = user?.firstName || "";

  // Fetch session init data on mount
  useEffect(() => {
    if (!sessionInit && !sessionInitLoading) {
      dispatch(fetchSessionInit());
    }
  }, [dispatch, sessionInit, sessionInitLoading]);

  // Fetch machine-scoped session init when machineContext changes
  useEffect(() => {
    if (machineContext?.machineId) {
      dispatch(fetchMachineSessionInit(machineContext.machineId));
    }
  }, [dispatch, machineContext?.machineId]);

  // If machine context is set, render machine-specific welcome
  if (machineContext) {
    return (
      <MidasMachineWelcome
        key={machineContext.machineId}
        machineContext={machineContext}
        botSessionInit={machineSessionInit}
        botSessionInitLoading={machineSessionInitLoading}
        onSuggestionClick={onSuggestionClick}
      />
    );
  }

  const summary = sessionInit?.summary;
  const greeting = sessionInit?.greeting
    ? sessionInit.greeting
    : translate("midas.welcome.greeting", { firstName });

  // Counts for pills
  const openSRs = summary?.open_service_requests ?? 0;
  const machinesCount = summary?.machines_count ?? 0;
  const unreadNotifs = summary?.unread_notifications ?? 0;
  const hasSummary = summary !== null && summary !== undefined;

  return (
    <Box
      key="platform"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        padding: isPhone ? "24px 16px" : "40px 24px",
        overflow: "auto",
        maxWidth: "520px",
        mx: "auto",
        width: "100%",
      }}
    >
      {/* 1. Platform Identity block */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          animation: "midasWelcomeAvatar 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        }}
      >
        {/* Dark MIDAS badge with gold M */}
        <Box
          sx={{
            width: "44px",
            height: "44px",
            borderRadius: "14px",
            backgroundColor: midas.badgeBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: dt.font,
              fontSize: "22px",
              fontWeight: 800,
              color: midas.gold,
              lineHeight: 1,
            }}
          >
            M
          </Box>
        </Box>

        {/* Greeting */}
        {sessionInitLoading ? (
          <Skeleton variant="text" width={200} sx={{ bgcolor: "rgba(0,0,0,0.06)" }} />
        ) : (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "20px",
              fontWeight: 700,
              color: midas.textDark,
              letterSpacing: "-0.03em",
              textAlign: "center",
            }}
          >
            {greeting}
          </Box>
        )}

        {/* Subtitle */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 400,
            color: midas.textMuted,
            textAlign: "center",
          }}
        >
          {translate("midas.welcome.platformSubtitle")}
        </Box>
      </Box>

      {/* 2. Status pills */}
      {!sessionInitLoading && hasSummary && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
            mt: "20px",
            animation: "midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both",
          }}
        >
          {/* Requests pill */}
          <Box sx={pillSx}>
            <ClockIcon />
            <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: midas.srBlue }}>
              {openSRs}
            </Box>
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: midas.textGray }}>
              {translate("midas.welcome.pillRequests", { count: openSRs })}
            </Box>
          </Box>

          {/* Machines pill */}
          <Box sx={pillSx}>
            <MachineIcon />
            <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: midas.machineGreen }}>
              {machinesCount}
            </Box>
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: midas.textGray }}>
              {translate("midas.welcome.pillMachines", { count: machinesCount })}
            </Box>
          </Box>

          {/* Notifications pill */}
          <Box sx={pillSx}>
            <BellIcon />
            <Box component="span" sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: midas.notifAmber }}>
              {unreadNotifs}
            </Box>
            <Box component="span" sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 400, color: midas.textGray }}>
              {translate("midas.welcome.pillNotifications", { count: unreadNotifs })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Loading skeleton for pills */}
      {sessionInitLoading && (
        <Box sx={{ display: "flex", gap: "10px", mt: "20px" }}>
          <Skeleton variant="rounded" width={120} height={32} sx={{ borderRadius: "20px", bgcolor: "rgba(0,0,0,0.06)" }} />
          <Skeleton variant="rounded" width={100} height={32} sx={{ borderRadius: "20px", bgcolor: "rgba(0,0,0,0.06)" }} />
          <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: "20px", bgcolor: "rgba(0,0,0,0.06)" }} />
        </Box>
      )}

      {/* 3. Suggestion cards (3 cards, 2+1 layout) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          width: "100%",
          mt: "24px",
        }}
      >
        {platformSuggestions.map((key, index) => {
          const text = translate(key);
          return (
            <Box
              key={key}
              onClick={() => onSuggestionClick(text)}
              sx={{
                flex: "1 1 calc(50% - 4px)",
                minWidth: isPhone ? "100%" : "200px",
                backgroundColor: "#FFFFFF",
                border: `1px solid ${midas.pillBorder}`,
                borderRadius: "10px",
                padding: "12px 14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                animation: `midasFadeUp 0.35s cubic-bezier(0.4, 0, 0.2, 1) ${0.3 + index * 0.08}s both`,
                "&:hover": {
                  borderColor: midas.gold,
                  backgroundColor: "rgba(212, 168, 67, 0.04)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                },
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <SuggestionIcon />
              </Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 400,
                  color: midas.cardText,
                  lineHeight: 1.4,
                }}
              >
                {text}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Shared pill style
const pillSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  border: "1px solid #E5E7EB",
  padding: "6px 14px",
};

export default MidasWelcome;
